// Tarefas (/todolist) — lista pessoal, guardada no navegador.
// Mockup: "Tarefas (/todolist · lista pessoal, salva no navegador)".
//
// Não há backend: tudo vive no localStorage, e o rodapé avisa disso, porque
// "some ao trocar de máquina" é a primeira dúvida de quem usa.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import CheckIcon from "@material-ui/icons/Check";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DevicesIcon from "@material-ui/icons/Devices";
import { makeStyles, alpha } from "@material-ui/core/styles";
import moment from "moment";

import MainContainer from "../../components/MainContainer";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import { i18n } from "../../translate/i18n";
import useCampaignPageStyles from "../Campaigns/campaignPageStyles";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS } from "../../styles/aerosTokens";
import {
  CHAVE,
  contaPendentes,
  criaTarefa,
  leTarefas,
  ordenaTarefas,
} from "./todoRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  return {
    headerSubtitle: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      marginTop: 2,
    },
    addRow: { display: "flex", gap: 10, alignItems: "center" },
    lista: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      overflow: "hidden",
      marginTop: 16,
    },
    item: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "12px 16px",
      borderBottom: `1px solid ${
        isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"
      }`,
      "&:last-child": { borderBottom: "none" },
    },
    itemAberto: {
      backgroundColor: isLight ? "rgba(15,20,40,0.02)" : "rgba(255,255,255,0.02)",
    },
    // Caixa de marcar: gradiente quando concluída, contorno quando pendente.
    caixa: {
      width: 20,
      height: 20,
      borderRadius: 6,
      flex: "none",
      padding: 0,
      minWidth: 20,
      border: `1px solid ${
        isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.25)"
      }`,
      "& .MuiSvgIcon-root": { fontSize: 14, color: "#07080D" },
    },
    caixaFeita: {
      border: "1px solid transparent",
      background: `linear-gradient(135deg, ${AEROS.defaultPrimary}, ${AEROS.cyan})`,
    },
    texto: { flex: 1, minWidth: 0 },
    textoLinha: {
      fontSize: 13.5,
      color: theme.palette.text.primary,
      wordBreak: "break-word",
    },
    textoFeito: {
      color: theme.palette.aeros.textTertiary,
      textDecoration: "line-through",
    },
    data: { fontSize: 11, color: theme.palette.aeros.textTertiary, marginTop: 2 },
    vazio: {
      padding: "28px 16px",
      textAlign: "center",
      fontSize: 12.5,
      color: theme.palette.aeros.textTertiary,
    },
    nota: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginTop: 12,
      fontSize: 12,
      color: theme.palette.aeros.textTertiary,
      "& .MuiSvgIcon-root": { fontSize: 15, flex: "none" },
    },
    contador: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      backgroundColor: alpha(AEROS.defaultPrimary, 0.14),
      borderRadius: 99,
      padding: "2px 10px",
      flex: "none",
    },
  };
});

const ToDoList = () => {
  const c = useCampaignPageStyles();
  const m = useAerosModalStyles();
  const classes = useStyles();

  const [tarefas, setTarefas] = useState([]);
  const [texto, setTexto] = useState("");
  const [editando, setEditando] = useState(null);
  const [excluindo, setExcluindo] = useState(null);

  useEffect(() => {
    try {
      setTarefas(leTarefas(localStorage.getItem(CHAVE)));
    } catch (err) {
      // Navegador com armazenamento bloqueado: a tela abre vazia em vez de
      // quebrar.
      setTarefas([]);
    }
  }, []);

  const salva = useCallback((proximas) => {
    setTarefas(proximas);
    try {
      localStorage.setItem(CHAVE, JSON.stringify(proximas));
    } catch (err) {
      // Sem espaço ou em modo privado: a lista segue na memória da sessão.
    }
  }, []);

  const ordenadas = useMemo(() => ordenaTarefas(tarefas), [tarefas]);
  const pendentes = contaPendentes(tarefas);

  const handleAdicionar = () => {
    if (editando) {
      const proximas = tarefas.map((t) =>
        t.id === editando ? { ...t, text: texto.trim() } : t
      );
      salva(proximas.filter((t) => t.text));
      setEditando(null);
      setTexto("");
      return;
    }
    const nova = criaTarefa(texto);
    if (!nova) return;
    salva([nova, ...tarefas]);
    setTexto("");
  };

  const handleAlternar = (tarefa) =>
    salva(
      tarefas.map((t) => (t.id === tarefa.id ? { ...t, done: !t.done } : t))
    );

  const handleEditar = (tarefa) => {
    setEditando(tarefa.id);
    setTexto(tarefa.text);
  };

  const handleExcluir = () => {
    if (!excluindo) return;
    salva(tarefas.filter((t) => t.id !== excluindo.id));
    if (editando === excluindo.id) {
      setEditando(null);
      setTexto("");
    }
    setExcluindo(null);
  };

  return (
    <MainContainer>
      <AerosConfirmModal
        open={Boolean(excluindo)}
        onClose={() => setExcluindo(null)}
        onConfirm={handleExcluir}
        icon={<DeleteOutlineIcon />}
        tone="danger"
        title={i18n.t("aeros.todo.confirmTitle")}
        description={excluindo?.text}
        confirmLabel={i18n.t("aeros.todo.delete")}
      />

      <div className={c.pageHeader}>
        <div>
          <div className={c.pageTitle}>
            {i18n.t("aeros.todo.title")}{" "}
            {pendentes > 0 && (
              <span className={classes.contador}>
                {i18n.t(
                  pendentes === 1 ? "aeros.todo.pendingOne" : "aeros.todo.pending",
                  { count: pendentes }
                )}
              </span>
            )}
          </div>
          <div className={classes.headerSubtitle}>
            {i18n.t("aeros.todo.subtitle")}
          </div>
        </div>
      </div>

      <div className={c.pageBody}>
        <div className={classes.addRow}>
          <TextField
            fullWidth
            variant="outlined"
            className={m.input}
            placeholder={i18n.t("aeros.todo.placeholder")}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAdicionar();
              if (e.key === "Escape" && editando) {
                setEditando(null);
                setTexto("");
              }
            }}
          />
          <Button
            variant="contained"
            color="primary"
            className={c.gradientButton}
            startIcon={<AddIcon />}
            onClick={handleAdicionar}
            disabled={!texto.trim()}
          >
            {i18n.t(editando ? "aeros.todo.save" : "aeros.todo.add")}
          </Button>
        </div>

        <div className={classes.lista}>
          {ordenadas.length === 0 ? (
            <div className={classes.vazio}>{i18n.t("aeros.todo.empty")}</div>
          ) : (
            ordenadas.map((t) => (
              <div
                className={`${classes.item} ${
                  t.done ? "" : classes.itemAberto
                }`}
                key={t.id}
              >
                <IconButton
                  className={`${classes.caixa} ${
                    t.done ? classes.caixaFeita : ""
                  }`}
                  onClick={() => handleAlternar(t)}
                  aria-label={i18n.t(
                    t.done ? "aeros.todo.markOpen" : "aeros.todo.markDone"
                  )}
                  aria-pressed={t.done}
                >
                  {t.done && <CheckIcon />}
                </IconButton>

                <div className={classes.texto}>
                  <div
                    className={`${classes.textoLinha} ${
                      t.done ? classes.textoFeito : ""
                    }`}
                  >
                    {t.text}
                  </div>
                  {t.createdAt && (
                    <div className={classes.data}>
                      {moment(t.createdAt).format("DD/MM/YYYY HH:mm")}
                    </div>
                  )}
                </div>

                <Tooltip title={i18n.t("aeros.todo.edit")}>
                  <IconButton
                    className={c.rowActionButton}
                    onClick={() => handleEditar(t)}
                    aria-label={i18n.t("aeros.todo.edit")}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={i18n.t("aeros.todo.delete")}>
                  <IconButton
                    className={`${c.rowActionButton} ${c.rowActionButtonDanger}`}
                    onClick={() => setExcluindo(t)}
                    aria-label={i18n.t("aeros.todo.delete")}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              </div>
            ))
          )}
        </div>

        <div className={classes.nota}>
          <DevicesIcon />
          {i18n.t("aeros.todo.localNote")}
        </div>
      </div>
    </MainContainer>
  );
};

export default ToDoList;
